import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../../../js/authToken';
import API_BASE_URL from '../../../../js/urlHelper';
import Pagination from './Pagination';
import ConfirmationModal from '../../../Reutilizables/ConfirmationModal';

const Productos = () => {
  const defaultFormData = {
    nombre: '',
    rango_tasa: '',
    tipo_producto: 'RECURRENTE',
    activo: true,
  };
  const [productos, setProductos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(defaultFormData);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmState, setConfirmState] = useState({ show: false, producto: null });

  const resetFormState = () => {
    setShowForm(false);
    setFormData(defaultFormData);
    setEditingId(null);
  };

  // Fetch products
  const fetchProductos = async (page = 1, search = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/admin/productos?page=${page}&per_page=${itemsPerPage}&search=${search}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (!response.ok) throw new Error('Error al cargar productos');
      const data = await response.json();
      setProductos(data.productos || []);
      setCurrentPage(data.current_page);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total_items || 0);
      return data;
    } catch (err) {
      setError(err.message || 'Error al cargar productos');
      setTotalPages(1);
      setTotalItems(0);
      return { total_pages: 1, total_items: 0 };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductos(currentPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, searchTerm]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${API_BASE_URL}/api/admin/productos/${editingId}`
        : `${API_BASE_URL}/api/admin/productos`;
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar producto');
      }

      setFormData(defaultFormData);
      setEditingId(null);
      setShowForm(false);
      await fetchProductos(1, searchTerm);
    } catch (err) {
      setError(err.message || 'Error al guardar producto');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = async (id) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/productos/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Error al cargar producto');
      const data = await response.json();
      setFormData({
        ...defaultFormData,
        ...data.producto,
        activo: data.producto?.activo !== false,
      });
      setEditingId(id);
      setShowForm(true);
    } catch (err) {
      setError(err.message || 'Error al cargar producto');
    }
  };

  const openDeactivateConfirm = (producto) => {
    setConfirmState({ show: true, producto });
  };

  const closeDeactivateConfirm = () => {
    setConfirmState({ show: false, producto: null });
  };

  // Handle product deactivation
  const handleDeactivate = async () => {
    const producto = confirmState.producto;
    if (!producto) return;

    setLoading(true);
    setError('');
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/productos/${producto.idProducto}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Error al desactivar producto');
      await fetchProductos(currentPage, searchTerm);
    } catch (err) {
      setError(err.message || 'Error al desactivar producto');
    } finally {
      setLoading(false);
      closeDeactivateConfirm();
    }
  };

  // Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle items per page
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const indexOfFirstItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalItems);
  const currentItems = productos;

  const fieldLabelClass =
    'mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600';
  const fieldInputClass =
    'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200';
  const actionPrimaryClass =
    'rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50';
  const actionNeutralClass =
    'rounded-xl bg-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-300';
  const getTipoProductoLabel = (tipoProducto) => (
    tipoProducto === 'CAMPANA' ? 'Campaña' : 'Recurrente'
  );

  return (
    <div className="space-y-6 p-4">
      <section className="ui-card relative overflow-hidden px-6 py-7 md:px-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_88%_10%,rgba(217,44,52,0.12),transparent_38%)]" />
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-red-600">Catálogo comercial</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900 md:text-3xl">Gestión de productos</h1>
          <p className="mt-2 text-sm text-slate-600">
            Administra productos y su rango de tasa con un flujo de alta, edición y búsqueda.
          </p>
        </div>
      </section>

      <section className="ui-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Operaciones de producto</h2>
          <button
            onClick={() => {
              if (showForm) {
                resetFormState();
                return;
              }
              setFormData(defaultFormData);
              setEditingId(null);
              setShowForm(true);
            }}
            className={`${showForm ? 'bg-slate-600 hover:bg-slate-700' : 'bg-red-600 hover:bg-red-700'} inline-flex w-full items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-white transition sm:w-auto`}
          >
            {showForm ? 'CERRAR FORMULARIO' : 'AGREGAR PRODUCTO'}
          </button>
        </div>
      </section>

      {showForm && (
        <section className="ui-card overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <h3 className="text-base font-semibold text-slate-900">
              {editingId ? 'EDITAR PRODUCTO' : 'AGREGAR PRODUCTO'}
            </h3>
            <button
              onClick={resetFormState}
              className="text-slate-500 transition hover:text-slate-700"
              aria-label="Cerrar formulario"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-4">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className={fieldLabelClass}>NOMBRE</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    placeholder="Ej. CREDIVUELVE"
                    className={fieldInputClass}
                    required
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>RANGO DE TASA</label>
                  <input
                    type="text"
                    name="rango_tasa"
                    value={formData.rango_tasa}
                    onChange={handleInputChange}
                    placeholder="Ej. 13%-18%"
                    className={fieldInputClass}
                    required
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>TIPO DE PRODUCTO</label>
                  <select
                    name="tipo_producto"
                    value={formData.tipo_producto}
                    onChange={handleInputChange}
                    className={fieldInputClass}
                    required
                  >
                    <option value="RECURRENTE">RECURRENTE</option>
                    <option value="CAMPANA">CAMPAÑA</option>
                  </select>
                </div>
                <div className="flex flex-col justify-end">
                  <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      name="activo"
                      checked={!!formData.activo}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                    />
                    Activo
                  </label>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <button type="submit" disabled={loading} className={actionPrimaryClass}>
                  {loading ? 'GUARDANDO...' : editingId ? 'ACTUALIZAR' : 'GUARDAR'}
                </button>
                <button
                  type="button"
                  onClick={resetFormState}
                  className={actionNeutralClass}
                >
                  CANCELAR
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      <section className="ui-card p-4">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Productos registrados</h2>
          <span className="text-sm text-slate-600">Total: {totalItems}</span>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
          <div>
            <label className={fieldLabelClass}>Buscar producto</label>
            <input
              type="text"
              placeholder="BUSCAR POR ID O NOMBRE"
              value={searchTerm}
              onChange={handleSearchChange}
              className={fieldInputClass}
            />
          </div>
          <div>
            <label className={fieldLabelClass}>Items por página</label>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className={fieldInputClass}
            >
              <option value="5">5 POR PÁGINA</option>
              <option value="10">10 POR PÁGINA</option>
              <option value="20">20 POR PÁGINA</option>
              <option value="50">50 POR PÁGINA</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="py-6 text-center text-sm font-medium text-slate-600">CARGANDO PRODUCTOS...</div>
        ) : productos.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 py-6 text-center text-sm text-slate-600">
            {searchTerm
              ? 'No se encontraron resultados para la búsqueda.'
              : 'No hay productos registrados.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">Rango de tasa</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {currentItems.map((producto) => (
                    <tr key={producto.idProducto} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{producto.idProducto}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900">{producto.nombre}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{producto.rango_tasa}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{getTipoProductoLabel(producto.tipo_producto)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${producto.activo ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                          {producto.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(producto.idProducto)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-slate-700 transition hover:bg-slate-50"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => openDeactivateConfirm(producto)}
                            disabled={!producto.activo}
                            className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
                          >
                            Desactivar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </section>
      <ConfirmationModal
        show={confirmState.show}
        title="¿Confirmar desactivación del producto?"
        message="¿Estás seguro de desactivar este producto? No aparecerá en la calculadora para nuevos créditos."
        onClose={closeDeactivateConfirm}
        onConfirm={handleDeactivate}
        confirmText="Sí, desactivar"
        cancelText="No, cancelar"
      />
    </div>
  );
};

export default Productos;
